import React, { useContext, useState, useEffect } from "react";
import { makeStyles } from "@material-ui/core/styles";
import axios from "axios";

import { AuthContext } from "../contexts/AuthContext";
import { AdminContext } from "../contexts/AdminContext";
import { ClientContext } from "../contexts/ClientContext";
import { ScreenContext } from "../contexts/ScreenContext";
import ConfirmDialog from "../components/ConfirmDialog";

import Grid from "@material-ui/core/Grid";
import Typography from "@material-ui/core/Typography";
import Paper from "@material-ui/core/Paper";
import Button from "@material-ui/core/Button";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableContainer from "@material-ui/core/TableContainer";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import Dialog from "@material-ui/core/Dialog";
import DialogTitle from "@material-ui/core/DialogTitle";
import DialogContent from "@material-ui/core/DialogContent";
import DialogActions from "@material-ui/core/DialogActions";
import TextField from "@material-ui/core/TextField";
import Select from "@material-ui/core/Select";
import MenuItem from "@material-ui/core/MenuItem";
import FormControl from "@material-ui/core/FormControl";
import InputLabel from "@material-ui/core/InputLabel";
import IconButton from "@material-ui/core/IconButton";
import Chip from "@material-ui/core/Chip";
import LinearProgress from "@material-ui/core/LinearProgress";
import Box from "@material-ui/core/Box";
import Tooltip from "@material-ui/core/Tooltip";
import Autocomplete from "@material-ui/lab/Autocomplete";

import AddIcon from "@material-ui/icons/Add";
import EditIcon from "@material-ui/icons/Edit";
import BlockIcon from "@material-ui/icons/Block";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(2),
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: theme.spacing(2),
  },
  tableContainer: {
    marginTop: theme.spacing(2),
  },
  table: {
    '& .MuiTableCell-head': {
      fontWeight: 600,
      backgroundColor: theme.palette.grey[100],
    },
  },
  statusChip: {
    fontWeight: 500,
  },
  dialogContent: {
    minWidth: 400,
    [theme.breakpoints.down('sm')]: {
      minWidth: 'auto',
    },
  },
}));

const Users = () => {
  const classes = useStyles();
  const { user, token } = useContext(AuthContext);
  const { users, loadUsers } = useContext(AdminContext);
  const { clients, loadClients } = useContext(ClientContext);
  const { setSnackbarMessage } = useContext(ScreenContext);

  const API_URL = process.env.REACT_APP_API_URL || "http://localhost:8080/";

  const [isLoading, setIsLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [formData, setFormData] = useState({
    id: null,
    first_name: '',
    last_name: '',
    email: '',
    username: '',
    password: '',
    type: 'user',
    client_id: 0,
    toggl_api: ''
  });

  // Confirm dialog state
  const [confirmDialog, setConfirmDialog] = useState({
    open: false,
    title: '',
    message: '',
    onConfirm: null,
  });

  useEffect(() => {
    if (user.type === 'super_admin') {
      loadUsers(false); // Load all users (active and inactive)
      loadClients();
    }
  }, []);

  // Check if user is active (username doesn't end with 'xx')
  const isUserActive = (username) => !username.endsWith('xx');

  const handleOpenDialog = (userToEdit = null) => {
    if (userToEdit) {
      setEditMode(true);
      setFormData({
        id: userToEdit.id,
        first_name: userToEdit.first_name,
        last_name: userToEdit.last_name,
        email: userToEdit.email,
        username: userToEdit.username,
        password: '', // Don't populate password
        type: userToEdit.type,
        client_id: userToEdit.client_id || 0,
        toggl_api: userToEdit.toggl_api || ''
      });
    } else {
      setEditMode(false);
      setFormData({
        id: null,
        first_name: '',
        last_name: '',
        email: '',
        username: '',
        password: '',
        type: 'user',
        client_id: 0,
        toggl_api: ''
      });
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setFormData({
      id: null,
      first_name: '',
      last_name: '',
      email: '',
      username: '',
      password: '',
      type: 'user',
      client_id: 0,
      toggl_api: ''
    });
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSaveUser = async () => {
    // Validation
    if (!formData.first_name || !formData.last_name || !formData.email || !formData.username) {
      setSnackbarMessage("Please fill in all required fields");
      return;
    }

    if (!editMode && !formData.password) {
      setSnackbarMessage("Password is required for new users");
      return;
    }

    setIsLoading(true);

    try {
      const endpoint = editMode ? 'update-user' : 'create-user';
      const payload = editMode && !formData.password 
        ? { ...formData, password: undefined } // Don't send password if not changing
        : formData;

      await axios.post(
        API_URL + endpoint,
        payload,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      setSnackbarMessage(`User ${editMode ? 'updated' : 'created'} successfully`);
      handleCloseDialog();
      loadUsers(false); // Reload all users (active and inactive)
    } catch (error) {
      console.error("Save user error:", error);
      setSnackbarMessage(error.response?.data?.error || `Failed to ${editMode ? 'update' : 'create'} user`);
    } finally {
      setIsLoading(false);
    }
  };

  const handleToggleStatus = async (userId, currentUsername) => {
    const isActive = isUserActive(currentUsername);
    const action = isActive ? 'deactivate' : 'activate';

    setConfirmDialog({
      open: true,
      title: `${action.charAt(0).toUpperCase() + action.slice(1)} User`,
      message: `Are you sure you want to ${action} this user?`,
      onConfirm: () => executeToggleStatus(userId, !isActive),
    });
  };

  const executeToggleStatus = async (userId, newActiveStatus) => {
    setConfirmDialog({ ...confirmDialog, open: false });
    setIsLoading(true);

    try {
      await axios.post(
        API_URL + 'toggle-user-status',
        {
          id: userId,
          active: newActiveStatus
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      setSnackbarMessage({ message: `User ${newActiveStatus ? 'activated' : 'deactivated'} successfully`, severity: 'success' });
      loadUsers(false); // Reload all users (active and inactive)
    } catch (error) {
      console.error("Toggle status error:", error);
      setSnackbarMessage({ message: error.response?.data?.error || `Failed to toggle user status`, severity: 'error' });
    } finally {
      setIsLoading(false);
    }
  };

  // Only super_admin can access this page
  if (user.type !== 'super_admin') {
    return (
      <div className={classes.root}>
        <Typography variant="h5" color="error">
          Access Denied
        </Typography>
        <Typography variant="body1">
          You don't have permission to access this page.
        </Typography>
      </div>
    );
  }

  return (
    <div className={classes.root}>
      <Box className={classes.header}>
        <Typography variant="h4">
          Manage Users
        </Typography>
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
        >
          Add User
        </Button>
      </Box>

      {isLoading && <LinearProgress />}

      <Paper className={classes.tableContainer}>
        <TableContainer>
          <Table className={classes.table}>
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Username</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Client ID</TableCell>
                <TableCell>Toggl API</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {users
                .sort((a, b) => {
                  // Sort active users first
                  const aActive = isUserActive(a.username);
                  const bActive = isUserActive(b.username);
                  if (aActive && !bActive) return -1;
                  if (!aActive && bActive) return 1;
                  return 0;
                })
                .map((u) => {
                const active = isUserActive(u.username);
                const hasTogglApi = u.toggl_api && u.toggl_api.trim().length > 0;
                return (
                  <TableRow key={u.id}>
                    <TableCell>{u.first_name} {u.last_name}</TableCell>
                    <TableCell>{u.username}</TableCell>
                    <TableCell>{u.email}</TableCell>
                    <TableCell>
                      <Chip 
                        label={u.type} 
                        size="small"
                        color={u.type === 'super_admin' ? 'secondary' : 'default'}
                      />
                    </TableCell>
                    <TableCell>{u.client_id}</TableCell>
                    <TableCell align="center">
                      {hasTogglApi ? (
                        <CheckCircleIcon style={{ color: '#4caf50' }} />
                      ) : (
                        <span>—</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={active ? 'Active' : 'Inactive'}
                        size="small"
                        className={classes.statusChip}
                        color={active ? 'primary' : 'default'}
                        icon={active ? <CheckCircleIcon /> : <BlockIcon />}
                      />
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title="Edit User">
                        <IconButton 
                          size="small" 
                          color="primary"
                          onClick={() => handleOpenDialog(u)}
                        >
                          <EditIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={active ? 'Deactivate User' : 'Activate User'}>
                        <IconButton 
                          size="small" 
                          color={active ? 'default' : 'primary'}
                          onClick={() => handleToggleStatus(u.id, u.username)}
                        >
                          {active ? <BlockIcon /> : <CheckCircleIcon />}
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Add/Edit User Dialog */}
      <Dialog 
      open={dialogOpen} 
      onClose={handleCloseDialog} 
      maxWidth="sm" 
      fullWidth 
      disableEnforceFocus={true}>
        <DialogTitle>
          {editMode ? 'Edit User' : 'Add New User'}
        </DialogTitle>
        <DialogContent className={classes.dialogContent}>
          <Grid container spacing={2} style={{ marginTop: 8 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                id="user-first-name"
                name="firstName"
                label="First Name"
                value={formData.first_name}
                onChange={(e) => handleInputChange('first_name', e.target.value)}
                fullWidth
                required
                autoComplete="given-name"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                id="user-last-name"
                name="lastName"
                label="Last Name"
                value={formData.last_name}
                onChange={(e) => handleInputChange('last_name', e.target.value)}
                fullWidth
                required
                autoComplete="family-name"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                id="user-email"
                name="email"
                label="Email"
                type="email"
                value={formData.email}
                onChange={(e) => handleInputChange('email', e.target.value)}
                fullWidth
                required
                autoComplete="email"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                id="user-username"
                name="username"
                label="Username"
                value={formData.username}
                onChange={(e) => handleInputChange('username', e.target.value)}
                fullWidth
                required
                autoComplete="username"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                id="user-password"
                name="password"
                label={editMode ? "Password (leave blank to keep current)" : "Password"}
                type="password"
                value={formData.password}
                onChange={(e) => handleInputChange('password', e.target.value)}
                fullWidth
                required={!editMode}
                autoComplete="new-password"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth required variant="outlined">
                <InputLabel id="user-type-label">User Type</InputLabel>
                <Select
                  labelId="user-type-label"
                  id="user-type"
                  name="userType"
                  label="User Type"
                  value={formData.type}
                  onChange={(e) => handleInputChange('type', e.target.value)}
                >
                  <MenuItem value="user">User</MenuItem>
                  <MenuItem value="admin">Admin</MenuItem>
                  <MenuItem value="client">Client</MenuItem>
                  <MenuItem value="super_admin">Super Admin</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Autocomplete
                options={[{ ID: 0, Name: 'None' }, ...clients]}
                getOptionLabel={(option) => `${option.ID} - ${option.Name}`}
                value={clients.find(c => c.ID === formData.client_id) || { ID: 0, Name: 'None' }}
                onChange={(event, newValue) => {
                  handleInputChange('client_id', newValue ? newValue.ID : 0);
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    id="user-client"
                    name="client"
                    label="Client"
                    placeholder="Search by ID or name..."
                  />
                )}
                fullWidth
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                id="user-toggl-api"
                name="togglApi"
                label="Toggl API Key (optional)"
                value={formData.toggl_api}
                onChange={(e) => handleInputChange('toggl_api', e.target.value)}
                fullWidth
                autoComplete="off"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>
            Cancel
          </Button>
          <Button 
            onClick={handleSaveUser} 
            color="primary" 
            variant="contained"
            disabled={isLoading}
          >
            {editMode ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmDialog
        open={confirmDialog.open}
        title={confirmDialog.title}
        message={confirmDialog.message}
        onConfirm={confirmDialog.onConfirm}
        onCancel={() => setConfirmDialog({ ...confirmDialog, open: false })}
        confirmText="Confirm"
        confirmColor="primary"
      />
    </div>
  );
};

export default Users;
